import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { downloadClientsApi } from "@/api/downloadClients";
import { grabsApi } from "@/api/grabs";
import { indexersApi } from "@/api/indexers";
import { moviesApi } from "@/api/movies";
import { musicApi } from "@/api/music";
import { tvApi } from "@/api/tv";
import { formatBytes, formatSpeed, nzbApi } from "@/api/nzb";
import { ApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui/PageHeader";
import { Status, type StatusTone } from "@/components/ui/Status";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/Button";
import type { GrabStatus } from "@/api/types";
import { cn } from "@/lib/cn";

/**
 * Editorial dashboard. The hierarchy is intentional:
 *
 *   1. Library hero  — three big media tiles (movies / tv / music) with
 *      inline secondary metric. The user actually cares about these.
 *   2. System pill   — small inline chip strip for indexers / clients,
 *      because the user cares about these less day-to-day.
 *   3. Live ops      — download speed + disk side-by-side.
 *   4. Recent grabs  — feed with status chips and inline actions.
 */
export function Dashboard() {
  const indexers = useQuery({ queryKey: ["indexers"], queryFn: indexersApi.list });
  const clients = useQuery({
    queryKey: ["download-clients"],
    queryFn: downloadClientsApi.list,
  });
  const movies = useQuery({ queryKey: ["movies"], queryFn: () => moviesApi.list() });
  const series = useQuery({ queryKey: ["series"], queryFn: tvApi.list });
  const musicStats = useQuery({
    queryKey: ["music", "stats"],
    queryFn: musicApi.stats,
  });
  const recentGrabs = useQuery({
    queryKey: ["grabs", "recent"],
    queryFn: () => grabsApi.list({ limit: 12 }),
  });
  const nzbStatus = useQuery({
    queryKey: ["nzb", "status"],
    queryFn: nzbApi.status,
    refetchInterval: 2000,
    retry: false,
  });
  const nzbDisk = useQuery({
    queryKey: ["nzb", "disk"],
    queryFn: nzbApi.disk,
    refetchInterval: 10000,
    retry: false,
  });

  return (
    <div className="space-y-10">
      <PageHeader
        eyebrow="Overview"
        title="Library, queue, and live ops."
        description="Everything in one place. Ratios, freshness, and the most recent indexer activity."
      />

      <LibraryHero
        movies={{
          total: movies.data?.length,
          loading: movies.isLoading,
          downloaded: movies.data?.filter((m) => m.has_file).length,
          monitored: movies.data?.filter((m) => m.monitored).length,
        }}
        series={{
          total: series.data?.length,
          loading: series.isLoading,
          monitored: series.data?.filter((s) => s.monitored).length,
        }}
        music={{
          loading: musicStats.isLoading,
          stats: musicStats.data,
        }}
      />

      <SystemStrip
        indexers={indexers.data?.length}
        indexersLoading={indexers.isLoading}
        clients={clients.data?.length}
        clientsLoading={clients.isLoading}
      />

      <section className="space-y-3">
        <h2 className="eyebrow">Live ops</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DownloadSpeedCard
            loading={nzbStatus.isLoading}
            error={nzbStatus.error}
            speedBps={nzbStatus.data?.speed_bps}
            paused={nzbStatus.data?.paused}
          />
          <DiskSpaceCard
            loading={nzbDisk.isLoading}
            error={nzbDisk.error}
            totalBytes={nzbDisk.data?.total_bytes}
            freeBytes={nzbDisk.data?.free_bytes}
            completeDir={nzbDisk.data?.complete_dir}
          />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-end justify-between">
          <h2 className="eyebrow">Recent activity</h2>
          <Link
            to="/nzb/queue"
            className="text-xs text-foreground/55 underline-offset-4 hover:text-accent hover:underline"
          >
            View queue →
          </Link>
        </div>
        {recentGrabs.isLoading ? (
          <Skeleton.Rows count={6} />
        ) : !recentGrabs.data || recentGrabs.data.length === 0 ? (
          <EmptyState
            glyph="search"
            title="No grabs yet"
            description="Add a movie, series, or artist and click Search & Grab to start a release."
            action={
              <Link to="/movies">
                <Button variant="secondary">Browse movies</Button>
              </Link>
            }
          />
        ) : (
          <RecentGrabsTable rows={recentGrabs.data} />
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library hero
// ---------------------------------------------------------------------------

function LibraryHero({
  movies,
  series,
  music,
}: {
  movies: {
    total: number | undefined;
    loading: boolean;
    downloaded: number | undefined;
    monitored: number | undefined;
  };
  series: {
    total: number | undefined;
    loading: boolean;
    monitored: number | undefined;
  };
  music: {
    loading: boolean;
    stats:
      | {
          artists: number;
          monitored_artists: number;
          albums: number;
          downloaded_albums: number;
          monitored_albums: number;
          tracks: number;
          downloaded_tracks: number;
        }
      | undefined;
  };
}) {
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <HeroTile
        to="/movies"
        eyebrow="Movies"
        primary={movies.total}
        loading={movies.loading}
        rows={[
          {
            label: "Downloaded",
            value: movies.downloaded ?? 0,
            tone: "good",
          },
          {
            label: "Monitored",
            value: movies.monitored ?? 0,
            tone: "neutral",
          },
        ]}
      />
      <HeroTile
        to="/tv"
        eyebrow="Series"
        primary={series.total}
        loading={series.loading}
        rows={[
          {
            label: "Monitored",
            value: series.monitored ?? 0,
            tone: "neutral",
          },
        ]}
      />
      <HeroTile
        to="/music"
        eyebrow="Artists"
        primary={music.stats?.artists}
        loading={music.loading}
        rows={
          music.stats
            ? [
                {
                  label: "Albums",
                  value: music.stats.albums,
                  tone: "neutral",
                },
                {
                  label: "Tracks downloaded",
                  value: `${music.stats.downloaded_tracks} / ${music.stats.tracks}`,
                  tone: "good",
                },
              ]
            : []
        }
      />
    </section>
  );
}

function HeroTile({
  to,
  eyebrow,
  primary,
  loading,
  rows,
}: {
  to: string;
  eyebrow: string;
  primary: number | string | undefined;
  loading: boolean;
  rows: { label: string; value: number | string; tone: StatusTone }[];
}) {
  return (
    <Link
      to={to}
      className="group relative overflow-hidden rounded-md border border-line bg-surface/50 p-5 transition hover:border-line-strong hover:bg-surface/70"
    >
      {/* faint brass corner-glow on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-accent/0 transition group-hover:bg-accent/10"
      />
      <div className="eyebrow">{eyebrow}</div>
      <div className="mt-3">
        {loading ? (
          <div className="skeleton h-12 w-24" />
        ) : (
          <div className="font-display text-5xl font-light leading-none tracking-display-tighter">
            {primary ?? 0}
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <ul className="mt-5 space-y-1.5 border-t border-line/50 pt-3 text-xs">
          {rows.map((r) => (
            <li
              key={r.label}
              className="flex items-center justify-between gap-2"
            >
              <Status tone={r.tone}>{r.label}</Status>
              <span className="font-mono tabular-nums text-foreground/85">
                {r.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// System strip — small "you have N of these" bar
// ---------------------------------------------------------------------------

function SystemStrip({
  indexers,
  indexersLoading,
  clients,
  clientsLoading,
}: {
  indexers: number | undefined;
  indexersLoading: boolean;
  clients: number | undefined;
  clientsLoading: boolean;
}) {
  return (
    <section className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface/30 px-4 py-3 text-xs">
      <SystemPill
        to="/settings/indexers"
        label="Indexers"
        value={indexers}
        loading={indexersLoading}
      />
      <SystemPill
        to="/settings/download-clients"
        label="Download clients"
        value={clients}
        loading={clientsLoading}
      />
      <SystemPill to="/settings/quality" label="Quality profiles" />
      <SystemPill to="/settings/tmdb" label="TMDB" />
    </section>
  );
}

function SystemPill({
  to,
  label,
  value,
  loading,
}: {
  to: string;
  label: string;
  value?: number | undefined;
  loading?: boolean;
}) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-2 rounded-full border border-line/70 bg-surface-2/40 px-3 py-1 text-foreground/70 transition hover:border-line-strong hover:bg-surface-2 hover:text-foreground"
    >
      <span>{label}</span>
      {loading ? (
        <span className="skeleton h-3 w-4" />
      ) : value !== undefined ? (
        <span className="font-mono tabular-nums text-foreground/85">
          {value}
        </span>
      ) : (
        <span className="font-mono text-foreground/40">→</span>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// NZB engine cards
// ---------------------------------------------------------------------------

function isDownloaderOff(err: unknown): boolean {
  return err instanceof ApiError && err.status === 503;
}

function DownloadSpeedCard({
  loading,
  error,
  speedBps,
  paused,
}: {
  loading: boolean;
  error: unknown;
  speedBps: number | undefined;
  paused: boolean | undefined;
}) {
  return (
    <Link
      to="/nzb/queue"
      className="group rounded-md border border-line bg-surface/50 p-5 transition hover:border-line-strong"
    >
      <div className="flex items-center justify-between">
        <div className="eyebrow">Download speed</div>
        {paused ? (
          <Status variant="chip" tone="warn">
            paused
          </Status>
        ) : !loading && !error && (speedBps ?? 0) > 0 ? (
          <Status variant="chip" tone="good" pulse>
            live
          </Status>
        ) : null}
      </div>
      <div className="mt-3 font-display text-3xl font-medium tabular-nums tracking-display-tight">
        {loading
          ? "…"
          : isDownloaderOff(error)
            ? <span className="text-foreground/40">Downloader off</span>
            : error
              ? "—"
              : formatSpeed(speedBps ?? 0)}
      </div>
    </Link>
  );
}

function DiskSpaceCard({
  loading,
  error,
  totalBytes,
  freeBytes,
  completeDir,
}: {
  loading: boolean;
  error: unknown;
  totalBytes: number | undefined;
  freeBytes: number | undefined;
  completeDir: string | undefined;
}) {
  const used =
    totalBytes !== undefined && freeBytes !== undefined
      ? totalBytes - freeBytes
      : null;
  const pctUsed =
    used !== null && totalBytes && totalBytes > 0
      ? Math.min(100, Math.max(0, Math.round((used / totalBytes) * 100)))
      : 0;
  const tone: StatusTone =
    pctUsed > 90 ? "bad" : pctUsed > 75 ? "warn" : "good";
  return (
    <div className="rounded-md border border-line bg-surface/50 p-5">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Disk space</div>
        {!loading && !error && totalBytes != null && (
          <span className="font-mono text-xs text-foreground/55">
            {pctUsed}% used
          </span>
        )}
      </div>
      {loading ? (
        <div className="mt-3 space-y-3">
          <div className="skeleton h-8 w-40" />
          <div className="skeleton h-1.5 w-full" />
        </div>
      ) : isDownloaderOff(error) ? (
        <div className="mt-3 font-display text-3xl text-foreground/40">
          Downloader off
        </div>
      ) : error || totalBytes == null || freeBytes == null ? (
        <div className="mt-3 font-display text-3xl">—</div>
      ) : (
        <>
          <div className="mt-3 font-display text-3xl font-medium tabular-nums tracking-display-tight">
            {formatBytes(freeBytes)}{" "}
            <span className="font-sans text-sm font-normal text-foreground/50">
              free of {formatBytes(totalBytes)}
            </span>
          </div>
          <div className="mt-3 h-1 overflow-hidden rounded bg-surface-2">
            <div
              className={cn(
                "h-full transition-all",
                tone === "good" && "bg-good",
                tone === "warn" && "bg-warn",
                tone === "bad" && "bg-bad",
              )}
              style={{ width: `${pctUsed}%` }}
            />
          </div>
          {completeDir && (
            <div
              className="mt-3 truncate font-mono text-[11px] text-foreground/45"
              title={completeDir}
            >
              {completeDir}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent grabs table — with status chips + inline actions.
// ---------------------------------------------------------------------------

const grabStatusTone: Record<GrabStatus, StatusTone> = {
  queued: "info",
  downloading: "info",
  completed: "good",
  imported: "good",
  failed: "bad",
  removed: "neutral",
};

function RecentGrabsTable({
  rows,
}: {
  rows: import("@/api/types").GrabRecord[];
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
      <table className="w-full text-xs">
        <thead className="bg-surface-2/50">
          <tr className="text-left">
            <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
              Release
            </th>
            <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
              Kind
            </th>
            <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
              Status
            </th>
            <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
              Indexer
            </th>
            <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
              When
            </th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line/60">
          {rows.map((g) => (
            <tr
              key={g.id}
              className="group transition hover:bg-surface-2/40"
            >
              <td className="max-w-md truncate px-3 py-2.5 font-mono text-[11px] leading-tight text-foreground/90">
                {g.release_title}
              </td>
              <td className="px-3 py-2.5 capitalize text-foreground/70">
                {g.media_kind}
              </td>
              <td className="px-3 py-2.5">
                <Status
                  variant="chip"
                  tone={grabStatusTone[g.status]}
                  pulse={g.status === "downloading"}
                >
                  {g.status}
                </Status>
              </td>
              <td className="px-3 py-2.5 text-foreground/70">
                {g.indexer_name ?? "—"}
              </td>
              <td className="px-3 py-2.5 font-mono text-foreground/55">
                {relativeTime(g.grabbed_at)}
              </td>
              <td className="w-10 px-2 py-2 text-right">
                {g.media_kind === "movie" && (
                  <Link
                    to="/movies/$id"
                    params={{ id: String(g.media_id) }}
                    className="invisible text-foreground/55 transition hover:text-accent group-hover:visible"
                    title="Open"
                  >
                    →
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
